import React, { useEffect, useMemo, useState } from 'react';
import {
  AddressV2,
  ResCol,
  SelectBloodGroup,
  SelectGender,
  SelectBoolean,
  CheckboxInput,
  UploadProfileImageV2
} from 'components';
import {
  Input,
  Form,
  Divider,
  Row,
  DatePicker,
  InputNumber,
  Checkbox,
  Modal,
  Tooltip,
  Col
} from 'antd';
import { DrawerEdit, FormItemWrap } from '@unicus/react-ui-kit';
import moment from 'moment';
import { MaskedInput } from 'antd-mask-input';
import {
  federalTaxNumberValidator,
  mobilePhoneValidator
} from 'util/validators';
import isNullOrEmpty from 'util/functions/isNullOrEmpty';
import {
  findByFederalTaxNumber,
  initializeCreate
} from 'stores/patients/patientActions';
import { formartOnlyNumber } from 'util/formatters';
import { useDispatch } from 'react-redux';
import useSegmentField from 'core/hooks/useSegmentField';
import { InfoCircleOutlined } from '@ant-design/icons';

const PatientEdit = (props) => {
  const { data } = props;
  const segmentFields = useSegmentField();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isRequiredResponsible, setIsRequiredResponsible] = useState(false);
  const [openResponsible, setOpenResponsible] = useState(false);

  const handleCalculeRequiredResponsible = (birthDate) => {
    if (!birthDate) {
      return;
    }

    setIsRequiredResponsible(moment().diff(moment(birthDate), 'year') < 18);
  };

  const handleBeforeBinding = ({ userId, birthDate, responsible, ...rest }) => {
    handleCalculeRequiredResponsible(birthDate);

    if (responsible != null) {
      setOpenResponsible(true);
    }

    return {
      ...rest,
      birthDate: birthDate ? moment(birthDate) : null,
      responsible
    };
  };

  const handleSubmit = ({ birthDate, responsible, ...rest }) => {
    if (!openResponsible) {
      responsible = null;
    }

    return {
      ...rest,
      birthDate: birthDate
        ? moment(birthDate)
            .utc()
            .toISOString()
        : null,
      responsible
    };
  };

  const isEditing = useMemo(() => !isNullOrEmpty(data.id, 'guid'), [data]);
  const hasUserId = useMemo(() => !isNullOrEmpty(data.userId, 'guid'), [data]);
  const hasFederalTaxNumber = useMemo(
    () => !!(data.federalTaxNumber && isEditing),
    [data]
  );

  const handleSearchFederalTaxNumberSuccess = ({ data: { result } }) => {
    setLoading(false);
    Modal.confirm({
      title: `Já existe um ${segmentFields.serviceRecipient.toLowerCase()} com o CPF informado. Deseja carregá-lo?`,
      cancelText: 'Não',
      okText: 'Sim',
      width: 600,
      onOk: () => {
        dispatch(initializeCreate(result));
      }
    });
  };

  const handleSearchFederalTaxNumberFail = () => {
    setLoading(false);
    return true;
  };

  const handleChangeFederalTaxNumber = (e) => {
    const federalTaxNumber = formartOnlyNumber(e.target.value);

    if (isEditing) {
      return;
    }

    if (federalTaxNumber.length === 11) {
      setLoading(true);
      dispatch(
        findByFederalTaxNumber(
          federalTaxNumber,
          handleSearchFederalTaxNumberSuccess,
          handleSearchFederalTaxNumberFail
        )
      );
    }
  };

  const handleChangeValues = ({ birthDate }, values) => {
    if (birthDate) {
      handleCalculeRequiredResponsible(birthDate);
    }
  };

  const handleChangeResponsible = () => {
    if (isRequiredResponsible) {
      setOpenResponsible(true);
      return;
    }

    setOpenResponsible(!openResponsible);
  };

  useEffect(() => {
    if (isRequiredResponsible) {
      handleChangeResponsible(true);
    }
  }, [isRequiredResponsible]);

  return (
    <DrawerEdit
      {...props}
      onBeforeBinding={handleBeforeBinding}
      onSubmit={handleSubmit}
      size="md"
      onValuesChange={handleChangeValues}
    >
      <Form>
        <Row className="no-padding-last">
          <ResCol>
            <Row className="no-padding-last" align="middle">
              <Col flex={1}>
                <Row>
                  <ResCol span={24}>
                    <FormItemWrap
                      name="name"
                      label="Nome"
                      autoFocus
                      required
                      whitespace
                    >
                      <Input maxLength={150} />
                    </FormItemWrap>

                    <FormItemWrap name="legalName" whitespace>
                      <CheckboxInput
                        placeholder="Nome civil"
                        hint="Informe o nome civil se for diferente do nome social"
                      >
                        <Input maxLength={150} />
                      </CheckboxInput>
                    </FormItemWrap>

                    <Row className="no-padding-last">
                      <ResCol span={12}>
                        <FormItemWrap
                          name="federalTaxNumber"
                          label="CPF"
                          validateTrigger="onBlur"
                          rules={[{ validator: federalTaxNumberValidator }]}
                          required
                          hasFeedback={loading}
                          validateStatus={loading ? 'validating' : undefined}
                        >
                          <MaskedInput
                            mask="111.111.111-11"
                            onChange={handleChangeFederalTaxNumber}
                            disabled={
                              hasFederalTaxNumber ||
                              (hasUserId && hasFederalTaxNumber)
                            }
                          />
                        </FormItemWrap>
                      </ResCol>
                      <ResCol span={12}>
                        <FormItemWrap
                          name="birthDate"
                          label="Data de nascimento"
                          suffix={
                            <InfoCircleOutlined className="gx-ml-1 gx-fs-sm" />
                          }
                          required
                        >
                          <DatePicker
                            format="DD/MM/YYYY"
                            disabledDate={(date) =>
                              moment(date).isAfter(moment())
                            }
                          />
                        </FormItemWrap>
                      </ResCol>
                    </Row>
                  </ResCol>
                </Row>
              </Col>
              <Col span={7}>
                <div className="gx-flex-row gx-align-items-center gx-justify-content-center">
                  <FormItemWrap name="profileImage">
                    <UploadProfileImageV2 />
                  </FormItemWrap>
                </div>
              </Col>
            </Row>
          </ResCol>
        </Row>

        <Checkbox
          className="gx-mb-2"
          onChange={handleChangeResponsible}
          checked={openResponsible}
          disabled={isRequiredResponsible}
        >
          <span>Responsável</span>
          <Tooltip
            title={`Informe o CPF do responsável financeiro, caso não seja o(a) ${segmentFields.serviceRecipient.toLowerCase()}.`}
          >
            <InfoCircleOutlined className="gx-ml-1 gx-fs-sm" />
          </Tooltip>
        </Checkbox>

        {openResponsible && (
          <Row>
            <ResCol span={12}>
              <FormItemWrap
                name={['responsible', 'federalTaxNumber']}
                validateTrigger="onBlur"
                rules={[
                  {
                    required: isRequiredResponsible,
                    message: 'CPF do responsável é obrigatório'
                  },
                  { validator: federalTaxNumberValidator }
                ]}
              >
                <MaskedInput
                  mask="111.111.111-11"
                  placeholder="CPF do responsável"
                />
              </FormItemWrap>
            </ResCol>
            <ResCol span={12}>
              <FormItemWrap
                name={['responsible', 'name']}
                rules={[
                  {
                    required: isRequiredResponsible,
                    message: 'Nome do responsável é obrigatório'
                  }
                ]}
                whitespace
              >
                <Input maxLength={150} placeholder="Nome do responsável" />
              </FormItemWrap>
            </ResCol>
          </Row>
        )}

        <Row>
          <ResCol span={10}>
            <FormItemWrap
              name="email"
              label="E-mail"
              type="email"
              validateTrigger="onBlur"
              whitespace
            >
              <Input maxLength={100} />
            </FormItemWrap>
          </ResCol>
          <ResCol span={7}>
            <FormItemWrap
              name="phoneNumber"
              label="Celular"
              required
              validateTrigger="onBlur"
              rules={[{ validator: mobilePhoneValidator }]}
            >
              <MaskedInput mask="(11) 11111-1111" />
            </FormItemWrap>
          </ResCol>
          <ResCol span={7}>
            <FormItemWrap
              name="gender"
              label="Sexo"
              initialValue="uninformed"
              required
            >
              <SelectGender />
            </FormItemWrap>
          </ResCol>
        </Row>

        {segmentFields.isHealthcareEnabled && (
          <Row>
            <ResCol span={12}>
              <FormItemWrap
                name={['affiliation', 'firstFullName']}
                label="Filiação 1"
                whitespace
              >
                <Input maxLength={150} placeholder="Ex: Nome do pai" />
              </FormItemWrap>
            </ResCol>
            <ResCol span={12}>
              <FormItemWrap
                name={['affiliation', 'secondFullName']}
                label="Filiação 2"
                whitespace
              >
                <Input maxLength={150} placeholder="Ex: Nome da mãe" />
              </FormItemWrap>
            </ResCol>
          </Row>
        )}

        <Row>
          <ResCol>
            <FormItemWrap name="comments" label="Observações" whitespace>
              <Input.TextArea maxLength={400} autoSize />
            </FormItemWrap>
          </ResCol>
        </Row>

        {segmentFields.isHealthcareEnabled && (
          <>
            <Divider orientation="left">Informações adicionais</Divider>
            <Row>
              <ResCol span={12}>
                <FormItemWrap
                  name={['healthInfo', 'bloodDonator']}
                  label="Doador de sangue"
                  initialValue={false}
                >
                  <SelectBoolean />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <FormItemWrap
                  name={['healthInfo', 'personWithDisability']}
                  label="PcD"
                  initialValue={false}
                >
                  <SelectBoolean />
                </FormItemWrap>
              </ResCol>
            </Row>

            <Row>
              <ResCol span={12}>
                <FormItemWrap
                  name={['healthInfo', 'blood']}
                  label="Tipo sanguíneo"
                  initialValue={undefined}
                >
                  <SelectBloodGroup allowClear />
                </FormItemWrap>
              </ResCol>
              <ResCol span={6}>
                <FormItemWrap
                  name={['healthInfo', 'height']}
                  label="Altura"
                  initialValue={undefined}
                >
                  <InputNumber decimalSeparator="," />
                </FormItemWrap>
              </ResCol>
              <ResCol span={6}>
                <FormItemWrap name={['healthInfo', 'weight']} label="Peso">
                  <InputNumber decimalSeparator="," />
                </FormItemWrap>
              </ResCol>
            </Row>
          </>
        )}

        <Divider orientation="left">Endereço</Divider>
        <AddressV2 required={false} />
        {hasUserId && !isEditing ? (
          <FormItemWrap
            name="dataConfirmed"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  if (value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error('Confirmação dos dados é obrigatório')
                  );
                }
              }
            ]}
          >
            <Checkbox>
              Os dados do {segmentFields.client.toLowerCase()} foram
              confirmados?
            </Checkbox>
          </FormItemWrap>
        ) : null}
      </Form>
    </DrawerEdit>
  );
};
export default PatientEdit;



